import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getTenant,
  getAmbulances,
  getTraccarDevices,
  updateAmbulance,
  Ambulance,
  TraccarDevice,
} from '../services/api';

const SYNC_INTERVAL_MS = 3 * 60 * 1000;
const INITIAL_DELAY_MS = 1000;

const normalizeDeviceId = (id: string | undefined | null): string =>
  (id ?? '').trim().toLowerCase();

export const syncAmbulanceStatusFromTraccarDevices = async (groupId: number): Promise<void> => {
  const [ambulancesResponse, devicesResponse] = await Promise.all([
    getAmbulances(1, 1000),
    getTraccarDevices(groupId),
  ]);

  const ambulances: Ambulance[] = ambulancesResponse.data.data || [];
  const devices: TraccarDevice[] = devicesResponse.data.data || [];

  const ambulanceByDeviceId = new Map<string, Ambulance>();
  ambulances.forEach((amb) => {
    const deviceId = normalizeDeviceId(amb.spec?.gpsDevice?.deviceId);
    if (deviceId) {
      ambulanceByDeviceId.set(deviceId, amb);
    }
  });

  const updates: Promise<unknown>[] = [];

  for (const device of devices) {
    const deviceKey = normalizeDeviceId(device.uniqueId);
    if (!deviceKey) continue;

    const ambulance = ambulanceByDeviceId.get(deviceKey);
    if (!ambulance) continue;

    const currentStatus = ambulance.spec?.status;
    if (currentStatus === 'on_duty') continue;

    const isOnline = device.status === 'online';

    if (isOnline && currentStatus === 'off_duty') {
      updates.push(
        updateAmbulance(ambulance._id, {
          spec: { status: 'available' },
        })
      );
    } else if (!isOnline && currentStatus !== 'off_duty' && currentStatus !== 'on_duty') {
      updates.push(
        updateAmbulance(ambulance._id, {
          spec: { status: 'off_duty' },
        })
      );
    }
  }

  await Promise.allSettled(updates);
};

export function useSyncAmbulanceStatusFromDevices(): void {
  const { user, isAuthenticated } = useAuth();
  const groupIdRef = useRef<number | null>(null);
  const runningRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.tenantId) {
      groupIdRef.current = null;
      return;
    }

    let cancelled = false;

    const runSync = async () => {
      if (runningRef.current || cancelled) return;
      runningRef.current = true;

      try {
        let groupId = groupIdRef.current;
        if (groupId == null) {
          const tenantResponse = await getTenant(user.tenantId!);
          const rawGroupId = tenantResponse.data?.data?.spec?.groupId;
          if (!rawGroupId) return;

          groupId = Number(rawGroupId);
          if (!Number.isInteger(groupId) || groupId <= 0) return;
          groupIdRef.current = groupId;
        }

        await syncAmbulanceStatusFromTraccarDevices(groupId);
      } catch (err) {
        console.error('[AmbulanceStatusSync] Failed to sync from Traccar devices:', err);
      } finally {
        runningRef.current = false;
      }
    };

    const initialTimeout = window.setTimeout(() => {
      void runSync();
    }, INITIAL_DELAY_MS);

    const intervalId = window.setInterval(() => {
      void runSync();
    }, SYNC_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimeout);
      window.clearInterval(intervalId);
      groupIdRef.current = null;
    };
  }, [isAuthenticated, user?.tenantId]);
}
